/**
 * User-interaction events from the analytics gap fill (follow-up to RFC-1577).
 *
 * Every helper here is called from the click / submit / select handler of a
 * console control — never from a render, an effect, a server response that the
 * user did not trigger, or a timer. Properties are ids, enums and buckets only
 * (no prompts, names, file names, URLs or exact credit amounts).
 */
import { createNadDebug } from '../utils/debugLog'
import { countBucket, creditsBucket } from './buckets'
import { track, type ClientAnalyticsEventName, type ClientAnalyticsEventProps } from './events'
import type {
  ActionResult,
  AllowanceLimitType,
  AllowancePeriod,
  ChatCardAction,
  ChatCardType,
  EditorEntryPoint,
  GenerationCancelStage,
  NodeToolbarControl,
  TeamActorRole,
  TimelineEditAction,
  TimelineTrackKind,
  WorkspaceViewMode,
} from './generated/analyticsEvents'
import { analyticsNodeType } from './nodeAnalytics'
import { projectTeamContext } from './teamContext'
import { newAnalyticsId } from './chatHeaderAnalytics'
import { noteToolbarAddToChat } from './chatReferenceOrigin'
import { outputVariantId } from './outputVariantId'
import { peekProjectVisitId } from './projectVisit'
import { currentWorkspaceViewMode } from './assetDownloadAnalytics'

const log = createNadDebug('dw:analytics-events:interaction')

/** Emit and log one interaction event. Never throws into product code. */
export function trackInteraction<N extends ClientAnalyticsEventName>(name: N, props: ClientAnalyticsEventProps[N]): void {
  try {
    log({ event: name, ...props })
    track(name, props)
  } catch (err) {
    log({ event: 'interaction-track-failed', name, error: String(err) })
  }
}

export function resultOf(ok: boolean): ActionResult {
  return ok ? 'success' : 'failure'
}

// ── Teams credits / quota ───────────────────────────────────────────────────

export function trackAllowanceAction(input: {
  teamId: string
  action: 'edit_opened' | 'set' | 'clear'
  ok?: boolean
  limitType?: AllowanceLimitType
  previousLimitType?: AllowanceLimitType
  period?: AllowancePeriod | null
  limitAmount?: number | null
  targetRole?: TeamActorRole
  actorRole?: TeamActorRole
}): void {
  trackInteraction('teams_member_allowance_action', {
    team_id: input.teamId,
    action: input.action,
    result: input.ok === undefined ? undefined : resultOf(input.ok),
    limit_type: input.limitType,
    previous_limit_type: input.previousLimitType,
    period: input.period ?? undefined,
    limit_bucket: input.limitAmount === null || input.limitAmount === undefined ? undefined : creditsBucket(input.limitAmount),
    target_member_role: input.targetRole,
    actor_role: input.actorRole,
  })
}

// ── Director chat ───────────────────────────────────────────────────────────

export function trackChatCard(projectId: string, cardType: ChatCardType, action: ChatCardAction, extra: { optionCount?: number; questionCount?: number } = {}): void {
  if (!projectId) return
  trackInteraction('chat_card_action', {
    project_id: projectId,
    card_type: cardType,
    action,
    option_count_bucket: extra.optionCount === undefined ? undefined : countBucket(extra.optionCount),
    question_count_bucket: extra.questionCount === undefined ? undefined : countBucket(extra.questionCount),
  })
}

export function trackExecutionPlan(projectId: string, option: string, nodeCount: number, totalCredits: number): void {
  if (!projectId || (option !== 'confirm' && option !== 'cancel')) return
  trackInteraction('chat_execution_plan_action', {
    project_id: projectId,
    action: option,
    node_count_bucket: countBucket(nodeCount),
    credits_bucket: creditsBucket(totalCredits),
  })
}

// ── Canvas ──────────────────────────────────────────────────────────────────

/** Toolbar action keys → control (+ option). Keys not listed (duplicate, delete, download, layout, colour, …) do not log here. */
const TOOLBAR_CONTROLS: Record<string, { control: NodeToolbarControl; option?: string }> = {
  addToChat: { control: 'add_to_chat' },
  'add-to-chat': { control: 'add_to_chat' },
  'save-to-library': { control: 'save_to_library' },
  'extract-frame-current': { control: 'extract_frame', option: 'current' },
  'extract-frame-first': { control: 'extract_frame', option: 'first' },
  'extract-frame-last': { control: 'extract_frame', option: 'last' },
  'extract-audio-raw': { control: 'extract_audio', option: 'raw' },
  'extract-audio-isolated-stems': { control: 'extract_audio', option: 'isolated_stems' },
  'extract-audio-dialogues': { control: 'extract_audio', option: 'dialogues' },
  'create-depth-video': { control: 'depth' },
  'extract-depth': { control: 'depth' },
  'extract-depth-with-audio': { control: 'depth' },
  'ai-clip': { control: 'ai_clips' },
  'upscale-hd': { control: 'upscale', option: 'hd' },
  'create-panorama': { control: 'panorama', option: 'create' },
  'panorama-extract': { control: 'panorama', option: 'extract' },
  'panorama-reset': { control: 'panorama', option: 'reset' },
  'four-angles': { control: 'location_angles', option: 'four' },
  'eight-angles': { control: 'location_angles', option: 'eight' },
  'current-view': { control: 'location_angles', option: 'current_view' },
  fullscreen: { control: 'fullscreen' },
  annotate: { control: 'annotate' },
}

export function toolbarControlFor(key: string): { control: NodeToolbarControl; option?: string } | undefined {
  return TOOLBAR_CONTROLS[key]
}

export function trackNodeToolbarAction(input: {
  projectId: string | null | undefined
  nodeId: string | null
  nodeType?: string
  key: string
  viewMode: WorkspaceViewMode
  /** History index of the node's picked output, when it has one. */
  pickedIndex?: number
}): void {
  const mapped = toolbarControlFor(input.key)
  if (!mapped || !input.projectId || !input.nodeId) return
  const interactionId = newAnalyticsId()
  // Add to chat: the prompt that carries this node reports the same id.
  if (mapped.control === 'add_to_chat') noteToolbarAddToChat(input.nodeId, interactionId)
  trackInteraction('canvas_node_toolbar_action', {
    project_id: input.projectId,
    project_visit_id: peekProjectVisitId(input.projectId) ?? undefined,
    node_id: input.nodeId,
    node_type: input.nodeType ? analyticsNodeType(input.nodeType) : undefined,
    variant_id: outputVariantId(input.nodeId, input.pickedIndex),
    control: mapped.control,
    option: mapped.option,
    entry_point: 'node_toolbar',
    interaction_id: interactionId,
    view_mode: input.viewMode,
  })
}

/** An `out_*` field that stores a variant history (`GeneratedImageWithHistory` & co). */
function isPickedHistory(value: object): value is { picked_index: number; history: object[] } {
  return 'picked_index' in value && typeof value.picked_index === 'number'
    && 'history' in value && Array.isArray(value.history)
}

/** History index of a node's picked output (its `out_*` history field), if any. */
export function pickedOutputIndex(data: object | null | undefined): number | undefined {
  if (!data) return undefined
  for (const [key, value] of Object.entries(data)) {
    if (!key.startsWith('out_') || typeof value !== 'object' || value === null) continue
    if (isPickedHistory(value)) return value.picked_index
  }
  return undefined
}

export function trackGenerationCancelled(input: { projectId: string | null | undefined; nodeId: string; nodeType?: string; stage: GenerationCancelStage; requestId?: string }): void {
  if (!input.projectId) return
  trackInteraction('canvas_generation_cancelled', {
    project_id: input.projectId,
    node_id: input.nodeId,
    node_type: input.nodeType ? analyticsNodeType(input.nodeType) : undefined,
    stage: input.stage,
    request_id: input.requestId,
  })
}

// The video editor open now (one at a time): its edits carry its session id.
let editorSession: { nodeId: string; id: string } | null = null

/**
 * `project_editor_viewed` (analytics doc name), on the user's Open editor click.
 * Starts the editor session whose id every timeline edit then carries.
 */
export function trackEditorOpened(input: {
  projectId: string | null | undefined
  canvasId?: string
  nodeId: string
  entryPoint: EditorEntryPoint
  editable: boolean
}): void {
  editorSession = { nodeId: input.nodeId, id: newAnalyticsId() }
  if (!input.projectId) return
  trackInteraction('project_editor_viewed', {
    project_id: input.projectId,
    canvas_id: input.canvasId || undefined,
    project_visit_id: peekProjectVisitId(input.projectId) ?? undefined,
    node_id: input.nodeId,
    editor_session_id: editorSession.id,
    entry_point: input.entryPoint,
    view_mode: currentWorkspaceViewMode(),
    editable: input.editable,
  })
}

export function trackTimelineEdit(projectId: string | null | undefined, nodeId: string | null | undefined, action: TimelineEditAction, trackKind?: TimelineTrackKind): void {
  if (!projectId) return
  trackInteraction('canvas_timeline_edit_action', {
    project_id: projectId,
    node_id: nodeId ?? undefined,
    editor_session_id: nodeId && editorSession?.nodeId === nodeId ? editorSession.id : undefined,
    action,
    track_kind: trackKind,
  })
}

// ── Project-scoped team context ─────────────────────────────────────────────

/** owner_type (+ team_id) of the project's owning team, for project-scoped events. */
export function projectOwnerProps(projectId: string | null | undefined): { owner_type?: 'personal' | 'team'; team_id?: string } {
  const ctx = projectTeamContext(projectId)
  return { owner_type: ctx.owner_type, team_id: ctx.team_id }
}
